import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import { toast as sonnerToast } from 'sonner'
import { toast } from '@/lib/toast'
import i18n from '@/i18n'
import { api, ApiError } from '@/lib/api'
import type { Contact, ContactListResponse, DuplicatesResponse, MergePayload } from '@/lib/types'

interface ContactsParams {
  page?: number
  limit?: number
  sort?: string
  order?: string
  q?: string
  circle_id?: string
  label_id?: string
}

export function useContacts(params: ContactsParams = {}) {
  const searchParams = new URLSearchParams()
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== '') searchParams.set(k, String(v))
  })
  const qs = searchParams.toString()

  return useQuery({
    queryKey: ['contacts', params],
    queryFn: () => api<ContactListResponse>(`/api/contacts${qs ? `?${qs}` : ''}`),
  })
}

export function useContact(id: string | undefined) {
  return useQuery({
    queryKey: ['contacts', id],
    queryFn: () => api<Contact>(`/api/contacts/${id}`),
    enabled: !!id,
  })
}

export function useCreateContact() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      api<Contact>('/api/contacts', { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contacts'] })
      toast.success(i18n.t('toast.contactCreated'))
    },
    onError: () => {
      toast.error(i18n.t('toast.error'))
    },
  })
}

export function useUpdateContact() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string } & Record<string, unknown>) =>
      api<Contact>(`/api/contacts/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    onMutate: async (variables) => {
      const { id, ...updates } = variables
      await qc.cancelQueries({ queryKey: ['contacts'] })

      // Snapshot single contact detail
      const previousContact = qc.getQueryData<Contact>(['contacts', id])
      if (previousContact) {
        qc.setQueryData<Contact>(['contacts', id], { ...previousContact, ...updates })
      }

      // Snapshot contact list queries
      const listQueries = qc.getQueriesData<ContactListResponse>({ queryKey: ['contacts'] })
      const previousLists = new Map(
        listQueries.filter(([key]) => {
          // Only match list queries (with params object), not detail or special keys
          const lastSegment = key[key.length - 1]
          return typeof lastSegment === 'object' || key.length === 1
        }),
      )

      for (const [key] of previousLists) {
        qc.setQueryData<ContactListResponse>(key, (old) => {
          if (!old) return old
          return {
            ...old,
            items: old.items.map((c) => (c.id === id ? { ...c, ...updates } : c)),
          }
        })
      }

      return { previousContact, previousLists, id }
    },
    onError: (_err, _variables, context) => {
      if (context) {
        if (context.previousContact) {
          qc.setQueryData(['contacts', context.id], context.previousContact)
        }
        if (context.previousLists) {
          for (const [key, data] of context.previousLists) {
            qc.setQueryData(key, data)
          }
        }
      }
      toast.error(i18n.t('toast.error'))
    },
    onSuccess: () => {
      toast.success(i18n.t('toast.contactUpdated'))
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['contacts'] })
    },
  })
}

export function useDeleteContact() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api<void>(`/api/contacts/${id}`, { method: 'DELETE' }),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: ['contacts'] })

      // Snapshot single contact detail
      const previousContact = qc.getQueryData<Contact>(['contacts', id])

      // Snapshot and optimistically remove from all list queries
      const listQueries = qc.getQueriesData<ContactListResponse>({ queryKey: ['contacts'] })
      const previousLists = new Map(
        listQueries.filter(([key]) => {
          const lastSegment = key[key.length - 1]
          return typeof lastSegment === 'object' || key.length === 1
        }),
      )

      for (const [key] of previousLists) {
        qc.setQueryData<ContactListResponse>(key, (old) => {
          if (!old) return old
          return {
            ...old,
            items: old.items.filter((c) => c.id !== id),
            total: old.total - 1,
          }
        })
      }

      return { previousContact, previousLists, id }
    },
    onSuccess: (_data, id) => {
      toast(i18n.t('toast.contactDeleted'), {
        action: {
          label: i18n.t('toast.undo'),
          onClick: () => {
            // Restore the contact via API
            api<Contact>(`/api/contacts/${id}/restore`, { method: 'POST' })
              .then(() => {
                qc.invalidateQueries({ queryKey: ['contacts'] })
                toast.success(i18n.t('toast.contactRestored'))
              })
              .catch(() => {
                toast.error(i18n.t('toast.error'))
              })
          },
        },
        duration: 5000,
      })
    },
    onError: (_err, _id, context) => {
      // Roll back optimistic removal
      if (context) {
        if (context.previousContact) {
          qc.setQueryData(['contacts', context.id], context.previousContact)
        }
        if (context.previousLists) {
          for (const [key, data] of context.previousLists) {
            qc.setQueryData(key, data)
          }
        }
      }
      toast.error(i18n.t('toast.error'))
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['contacts'] })
    },
  })
}

export function useImportLinkedIn() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (profileUrl: string): Promise<Contact> => {
      // Start the async import — returns immediately with a job ID
      const { jobId } = await api<{ jobId: string; status: string }>(
        '/api/contacts/import/linkedin',
        {
          method: 'POST',
          body: JSON.stringify({ profileUrl }),
        },
      )

      // Poll for completion
      const POLL_INTERVAL = 3_000
      const MAX_POLLS = 40 // ~2 minutes max
      for (let i = 0; i < MAX_POLLS; i++) {
        await new Promise((r) => setTimeout(r, POLL_INTERVAL))
        const result = await api<{ status: string; contact?: Contact; error?: string }>(
          `/api/contacts/import/linkedin/jobs/${jobId}`,
        )
        if (result.status === 'completed' && result.contact) {
          return result.contact
        }
        if (result.status === 'failed') {
          throw new Error(result.error || 'LinkedIn import failed')
        }
      }
      throw new Error('LinkedIn import timed out')
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contacts'] })
      toast.success(i18n.t('toast.contactImported'))
    },
    onError: () => {
      toast.error(i18n.t('toast.error'))
    },
  })
}

/**
 * Starts a LinkedIn import in the background and shows a persistent toast.
 * Returns immediately so the caller can navigate away.
 * The toast updates on completion with a clickable link to the new contact.
 */
export function useBackgroundImportLinkedIn() {
  const qc = useQueryClient()

  return useCallback(
    (profileUrl: string) => {
      const toastId = sonnerToast.loading(i18n.t('contacts.linkedInImporting'))

      // Fire and forget — polling happens detached from component lifecycle
      ;(async () => {
        try {
          const { jobId } = await api<{ jobId: string; status: string }>(
            '/api/contacts/import/linkedin',
            {
              method: 'POST',
              body: JSON.stringify({ profileUrl }),
            },
          )

          const POLL_INTERVAL = 3_000
          const MAX_POLLS = 40
          for (let i = 0; i < MAX_POLLS; i++) {
            await new Promise((r) => setTimeout(r, POLL_INTERVAL))
            const result = await api<{ status: string; contact?: Contact; error?: string }>(
              `/api/contacts/import/linkedin/jobs/${jobId}`,
            )
            if (result.status === 'completed' && result.contact) {
              qc.invalidateQueries({ queryKey: ['contacts'] })
              sonnerToast.success(i18n.t('toast.contactImported'), {
                id: toastId,
                duration: 8000,
                action: {
                  label: i18n.t('contacts.viewContact'),
                  onClick: () => {
                    window.location.href = `/contacts/${result.contact!.id}`
                  },
                },
              })
              return
            }
            if (result.status === 'failed') {
              throw new Error(result.error || 'LinkedIn import failed')
            }
          }
          throw new Error('LinkedIn import timed out')
        } catch {
          sonnerToast.error(i18n.t('contacts.linkedInError'), {
            id: toastId,
            duration: 5000,
          })
        }
      })()
    },
    [qc],
  )
}

/** Imports an X profile by URL or handle and shows a link to the created contact. */
export function useBackgroundImportX() {
  const qc = useQueryClient()

  return useCallback(
    (profile: string) => {
      const toastId = sonnerToast.loading(i18n.t('contacts.xImporting'))
      ;(async () => {
        try {
          const contact = await api<Contact>('/api/contacts/import/x', {
            method: 'POST',
            body: JSON.stringify({ profile }),
          })
          qc.invalidateQueries({ queryKey: ['contacts'] })
          sonnerToast.success(i18n.t('toast.contactImported'), {
            id: toastId,
            duration: 8000,
            action: {
              label: i18n.t('contacts.viewContact'),
              onClick: () => {
                window.location.href = `/contacts/${contact.id}`
              },
            },
          })
        } catch (err) {
          const code = err instanceof ApiError ? err.code : null
          const messageKey =
            code === 'INVALID_X_PROFILE'
              ? 'contacts.xInvalid'
              : code === 'X_PROFILE_NOT_FOUND'
                ? 'contacts.xErrorNotFound'
                : code === 'SERVICE_UNAVAILABLE'
                  ? 'contacts.xErrorUnavailable'
                  : code === 'X_API_ERROR'
                    ? 'contacts.xErrorApi'
                    : 'contacts.xError'
          sonnerToast.error(i18n.t(messageKey), { id: toastId, duration: 5000 })
        }
      })()
    },
    [qc],
  )
}

/**
 * Starts an Instagram import in the background and shows a persistent toast.
 * Accepts a profile URL or a bare handle. Returns immediately so the caller can
 * navigate away; the toast updates on completion with a link to the new contact.
 */
export function useBackgroundImportInstagram() {
  const qc = useQueryClient()

  return useCallback(
    (profile: string) => {
      const toastId = sonnerToast.loading(i18n.t('contacts.instagramImporting'))

      // Fire and forget — polling happens detached from component lifecycle
      ;(async () => {
        try {
          const { jobId } = await api<{ jobId: string; status: string }>(
            '/api/contacts/import/instagram',
            {
              method: 'POST',
              body: JSON.stringify({ profile }),
            },
          )

          const POLL_INTERVAL = 3_000
          const MAX_POLLS = 40
          for (let i = 0; i < MAX_POLLS; i++) {
            await new Promise((r) => setTimeout(r, POLL_INTERVAL))
            const result = await api<{ status: string; contact?: Contact; error?: string }>(
              `/api/contacts/import/instagram/jobs/${jobId}`,
            )
            if (result.status === 'completed' && result.contact) {
              qc.invalidateQueries({ queryKey: ['contacts'] })
              sonnerToast.success(i18n.t('toast.contactImported'), {
                id: toastId,
                duration: 8000,
                action: {
                  label: i18n.t('contacts.viewContact'),
                  onClick: () => {
                    window.location.href = `/contacts/${result.contact!.id}`
                  },
                },
              })
              return
            }
            if (result.status === 'failed') {
              throw new Error(result.error || 'Instagram import failed')
            }
          }
          throw new Error('Instagram import timed out')
        } catch {
          sonnerToast.error(i18n.t('contacts.instagramError'), {
            id: toastId,
            duration: 5000,
          })
        }
      })()
    },
    [qc],
  )
}

export interface BulkImportJob {
  profileUrl: string
  jobId: string | null
  status: 'running' | 'completed' | 'failed' | 'skipped'
  contact?: Contact | null
  error: string | null
}

export interface BulkImportResult {
  jobs: BulkImportJob[]
  allDone: boolean
}

export function useBulkImportLinkedIn() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (profileUrls: string[]): Promise<BulkImportResult> => {
      // Start all imports
      const { jobs: startedJobs } = await api<{ jobs: BulkImportJob[] }>(
        '/api/contacts/import/linkedin/bulk',
        {
          method: 'POST',
          body: JSON.stringify({ profileUrls }),
        },
      )

      // Collect job IDs that are actually running
      const runningIds = startedJobs
        .filter((j) => j.status === 'running' && j.jobId)
        .map((j) => j.jobId!)

      // Skipped entries (invalid URLs, quota exceeded) — keep as-is
      const skippedJobs = startedJobs
        .filter((j) => j.status === 'skipped')
        .map((j) => ({ ...j, status: 'failed' as const }))

      if (runningIds.length === 0) {
        return { jobs: skippedJobs, allDone: true }
      }

      // Poll for completion
      const POLL_INTERVAL = 3_000
      const MAX_POLLS = 60 // ~3 minutes max for bulk
      for (let i = 0; i < MAX_POLLS; i++) {
        await new Promise((r) => setTimeout(r, POLL_INTERVAL))
        const result = await api<BulkImportResult>(
          `/api/contacts/import/linkedin/bulk-status?jobIds=${runningIds.join(',')}`,
        )

        if (result.allDone) {
          return { jobs: [...result.jobs, ...skippedJobs], allDone: true }
        }
      }

      // Timed out — fetch one last time to get whatever completed
      const finalResult = await api<BulkImportResult>(
        `/api/contacts/import/linkedin/bulk-status?jobIds=${runningIds.join(',')}`,
      )
      return { jobs: [...finalResult.jobs, ...skippedJobs], allDone: finalResult.allDone }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contacts'] })
    },
    onError: () => {
      toast.error(i18n.t('toast.error'))
    },
  })
}

export function useDuplicates() {
  return useQuery({
    queryKey: ['contacts', 'duplicates'],
    queryFn: () => api<DuplicatesResponse>('/api/contacts/duplicates'),
  })
}

export function useMergeContacts() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: MergePayload) =>
      api<Contact>('/api/contacts/merge', { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contacts'] })
      qc.invalidateQueries({ queryKey: ['contacts', 'duplicates'] })
      toast.success(i18n.t('toast.contactsMerged'))
    },
    onError: () => {
      toast.error(i18n.t('toast.error'))
    },
  })
}

export function useDismissDuplicate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { contactId1: string; contactId2: string }) =>
      api<{ dismissed: boolean }>('/api/contacts/duplicates/dismiss', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contacts', 'duplicates'] })
      toast.success(i18n.t('toast.duplicateDismissed'))
    },
    onError: () => {
      toast.error(i18n.t('toast.error'))
    },
  })
}

// ---------------------------------------------------------------------------
// Bulk operations
// ---------------------------------------------------------------------------

export function useBulkAddToCircle() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { contact_ids: string[]; circle_id: string }) =>
      api<{ added: number }>('/api/contacts/bulk/add-to-circle', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['contacts'] })
      qc.invalidateQueries({ queryKey: ['circles'] })
      toast.success(i18n.t('batch.addedToCircle', { count: variables.contact_ids.length }))
    },
    onError: () => {
      toast.error(i18n.t('toast.error'))
    },
  })
}

export function useBulkAddLabel() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { contact_ids: string[]; label_id: string }) =>
      api<{ added: number }>('/api/contacts/bulk/add-label', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['contacts'] })
      toast.success(i18n.t('batch.labelApplied', { count: variables.contact_ids.length }))
    },
    onError: () => {
      toast.error(i18n.t('toast.error'))
    },
  })
}

export function useBulkDeleteContacts() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (contact_ids: string[]) =>
      api<{ deleted: number }>('/api/contacts/bulk/delete', {
        method: 'POST',
        body: JSON.stringify({ contact_ids }),
      }),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['contacts'] })
      qc.invalidateQueries({ queryKey: ['circles'] })
      toast.success(i18n.t('batch.deleted', { count: data.deleted }))
    },
    onError: () => {
      toast.error(i18n.t('toast.error'))
    },
  })
}
